import React from "react";
import TournamentMapDetail from "./TournamentMapDetail";

const HudIconBase: React.FC<{ className?: string; style?: React.CSSProperties; children: React.ReactNode }> = ({ className = "", style, children }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className={className} style={{ display: "block", color: "currentColor", ...style }} fill="none" xmlns="http://www.w3.org/2000/svg">{children}</svg>
);

const HudTrophyIcon: React.FC<{ className?: string; style?: React.CSSProperties }> = ({ className, style }) => (
  <HudIconBase className={className} style={style}>
    <path d="M8 4.75H16V8.5C16 10.7091 14.2091 12.5 12 12.5C9.79086 12.5 8 10.7091 8 8.5V4.75Z" fill="currentColor" fillOpacity="0.18" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
    <path d="M8 6H5.75C5.33579 6 5 6.33579 5 6.75V7.5C5 9.433 6.567 11 8.5 11" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M16 6H18.25C18.6642 6 19 6.33579 19 6.75V7.5C19 9.433 17.433 11 15.5 11" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12 12.5V16" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    <path d="M9 19H15" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    <path d="M9.5 16H14.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </HudIconBase>
);

type Props = { ctx: any };
const BigBlueTournamentOverlays: React.FC<Props> = ({ ctx }) => {
  const {
    getTournamentMapPresentation,
    getTournamentScopeBadge,
    handleTournamentMapRegistration,
    isDarkMode,
    isTournamentMapFilterOpen,
    isWorldTournamentMapOpen,
    isWorldwideTournament,
    markTournamentResultsSeen,
    myTournamentParticipation,
    props,
    selectedTournamentMapDetail,
    setIsTournamentMapFilterOpen,
    setIsWorldTournamentMapOpen,
    setSelectedTournamentMapDetail,
    setTournamentResultsSeenRevision,
    tournamentHubRows,
    tournamentMapRegistrationBusyId,
    worldTournamentRows
  } = ctx;
  return (<>
        <style>{`
          @keyframes roddoTournamentLiveBreathe {
            0%, 100% { box-shadow: 0 0 0 0 rgba(224, 174, 65, 0.00); }
            50% { box-shadow: 0 0 0 2px rgba(224, 174, 65, 0.08), 0 8px 22px rgba(0,0,0,0.10); }
          }
          .roddo-tournament-live-row { animation: roddoTournamentLiveBreathe 3.8s ease-in-out infinite; }
          @media (prefers-reduced-motion: reduce) {
            .roddo-tournament-live-row { animation: none !important; }
          }
        `}</style>
        {/* Worldwide tournaments: near-user map marker opens this global-only list. */}
        {worldTournamentRows.length > 0 ? (
          <>
            {isWorldTournamentMapOpen ? (
              <div
                className="pointer-events-auto absolute inset-0 z-[5100] bg-black/[0.035] dark:bg-black/[0.08]"
                onClick={() => setIsWorldTournamentMapOpen(false)}
                role="presentation"
              >
                <div
                  onClick={(event) => event.stopPropagation()}
                  className="absolute right-[68px] top-[118px] w-[min(76vw,330px)] overflow-hidden rounded-[20px] border shadow-[0_16px_44px_rgba(39,27,14,.22)] backdrop-blur-xl dark:shadow-[0_18px_48px_rgba(0,0,0,.46)]"
                  style={{
                    background: isDarkMode ? "rgba(13,20,35,.98)" : "rgba(255,250,242,.98)",
                    borderColor: isDarkMode ? "rgba(142,156,211,.20)" : "rgba(101,77,49,.13)",
                  }}
                >
                  <div className="flex items-center gap-3 border-b px-4 py-3 dark:border-white/10" style={{ borderColor: isDarkMode ? undefined : "rgba(101,77,49,.10)" }}>
                    <div
                      className="grid h-10 w-10 shrink-0 place-items-center rounded-[13px] border text-[18px]"
                      style={{
                        background: isDarkMode ? "#151D2E" : "#FFF3CB",
                        borderColor: isDarkMode ? "rgba(232,201,122,.22)" : "rgba(174,119,17,.14)",
                      }}
                      aria-hidden="true"
                    >🌎</div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="text-[14px] font-black" style={{ color: isDarkMode ? "#F2F6FC" : "#2D3957" }}>Worldwide Tournaments</div>
                        <span
                          className="rounded-full border px-2 py-0.5 text-[8px] font-black"
                          style={{
                            background: isDarkMode ? "rgba(246,200,74,.12)" : "rgba(255,242,196,.92)",
                            borderColor: isDarkMode ? "rgba(246,200,74,.25)" : "rgba(185,118,6,.18)",
                            color: isDarkMode ? "#FFE0A0" : "#95620A",
                          }}
                        >{worldTournamentRows.length} WORLD</span>
                      </div>
                      <div className="mt-0.5 text-[10px] font-semibold opacity-55">Global events · available anywhere</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsWorldTournamentMapOpen(false)}
                      className="grid h-8 w-8 place-items-center rounded-full border text-[14px] font-black"
                      style={{
                        background: isDarkMode ? "#111B2D" : "rgba(255,255,255,.72)",
                        borderColor: isDarkMode ? "rgba(142,156,211,.16)" : "rgba(101,77,49,.12)",
                        color: isDarkMode ? "#D9E2F2" : "#655A4F",
                      }}
                      aria-label="Close worldwide tournaments"
                    >×</button>
                  </div>

                  <div className="max-h-[330px] overflow-y-auto px-3 py-2.5">
                    <div className="space-y-1.5">
                      {worldTournamentRows.map(({ tournament, presentation }: any) => {
                        const id = String(tournament?.id || "");
                        const statusLabel = [presentation?.badge || presentation?.tooltip, presentation?.timer].filter(Boolean).join(" · ");
                        const fee = Number(tournament?.entryFeeShells ?? tournament?.entryFee ?? tournament?.shellEntryFee ?? 0);
                        const feeLabel = Number.isFinite(fee) && fee > 0 ? `${Math.round(fee)} shells` : "Free entry";
                        const approved = Number(tournament?.approvedParticipantCount);
                        const max = Number(tournament?.maxParticipants);
                        const anglerLabel = Number.isFinite(approved) && approved >= 0
                          ? Number.isFinite(max) && max > 0
                            ? `${Math.round(approved)}/${Math.round(max)} anglers`
                            : `${Math.round(approved)} anglers`
                          : "";
                        const coverUrl = String(tournament?.coverThumbUrl || tournament?.coverImageUrl || "").trim();
                        return (
                          <button
                            key={id}
                            type="button"
                            onClick={() => {
                              setIsWorldTournamentMapOpen(false);
                              setSelectedTournamentMapDetail(tournament);
                            }}
                            className="group flex min-h-[62px] w-full items-center gap-2.5 rounded-[14px] border px-2.5 py-2 text-left transition active:scale-[.99]"
                            style={{
                              background: isDarkMode ? "rgba(17,27,45,.82)" : "rgba(255,255,255,.72)",
                              borderColor: isDarkMode ? "rgba(142,156,211,.12)" : "rgba(101,77,49,.09)",
                            }}
                          >
                            <div
                              className="relative grid h-11 w-12 shrink-0 place-items-center overflow-hidden rounded-[11px] border"
                              style={{
                                background: isDarkMode ? "#151D2E" : "#FFF3CB",
                                borderColor: isDarkMode ? "rgba(232,201,122,.18)" : "rgba(101,77,49,.10)",
                              }}
                            >
                              {coverUrl ? <img src={coverUrl} alt="" className="absolute inset-0 h-full w-full object-cover" /> : <HudTrophyIcon className="h-5 w-5" style={{ color: isDarkMode ? "#FFE0A0" : "#A96E08" }} />}
                              <span className="absolute right-0.5 top-0.5 grid h-4 w-4 place-items-center rounded-full border text-[8px]" style={{ background: isDarkMode ? "#111B2D" : "#FFFDF8", borderColor: isDarkMode ? "rgba(255,224,160,.35)" : "rgba(169,110,8,.20)" }}>🌎</span>
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-[11.5px] font-black" style={{ color: isDarkMode ? "#F2F6FC" : "#2D3957" }}>{String(tournament?.name || "Worldwide Tournament")}</div>
                              <div className="mt-0.5 truncate text-[9.5px] font-bold" style={{ color: isDarkMode ? "#9FD9FF" : "#2F80ED" }}>{statusLabel || "Worldwide"}</div>
                              <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-[8.5px] font-semibold opacity-45">
                                <span>{feeLabel}</span>
                                {anglerLabel ? <><span>•</span><span>👥 {anglerLabel}</span></> : null}
                              </div>
                            </div>
                            <span className="text-base font-black opacity-35">›</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="border-t p-3 dark:border-white/10" style={{ borderColor: isDarkMode ? undefined : "rgba(101,77,49,.10)" }}>
                    <button
                      type="button"
                      onClick={() => {
                        setIsWorldTournamentMapOpen(false);
                        props.onEventsClick?.({ from: "big_blue_world_tournaments" });
                      }}
                      className="w-full rounded-[12px] border px-4 py-2.5 text-[11px] font-black"
                      style={{
                        background: isDarkMode ? "#F0C86D" : "#2F80ED",
                        borderColor: isDarkMode ? "rgba(240,200,109,.30)" : "rgba(47,128,237,.26)",
                        color: isDarkMode ? "#1A2233" : "#FFFFFF",
                      }}
                    >Open Tournament Dock</button>
                  </div>
                </div>
              </div>
            ) : null}
          </>
        ) : null}

        {props.isActive && selectedTournamentMapDetail ? (() => {
          const tournament = selectedTournamentMapDetail;
          const id = String(tournament?.id || "");
          const presentation = getTournamentMapPresentation(tournament);
          const scope = getTournamentScopeBadge(tournament);
          const participantStatus = String(myTournamentParticipation[id]?.status || "").toLowerCase();
          const registered = participantStatus === "approved";
          const pendingRegistration = participantStatus === "pending";
          const registrationClosesAt = Number(tournament?.registrationClosesAt || 0);
          const registrationOpen =
            String(tournament?.status || "").toLowerCase() === "registration_open" &&
            (!registrationClosesAt || registrationClosesAt > Date.now());
          const canWithdrawRegistration =
            (registered || pendingRegistration) &&
            ["approved", "registration_open"].includes(String(tournament?.status || "").toLowerCase());
          const registrationAction = registrationOpen || canWithdrawRegistration || pendingRegistration
            ? {
                label: tournamentMapRegistrationBusyId === id
                  ? "Working…"
                  : canWithdrawRegistration
                    ? pendingRegistration ? "Withdraw Request" : "Withdraw"
                    : pendingRegistration
                      ? "Awaiting Approval"
                      : String(tournament?.participationMode || "").toLowerCase() === "team"
                        ? "Register Duo"
                        : "Register",
                tone: canWithdrawRegistration ? "danger" as const : "success" as const,
                disabled: tournamentMapRegistrationBusyId === id || (pendingRegistration && !canWithdrawRegistration),
              }
            : null;

          return (
            <TournamentMapDetail
              tournament={tournament}
              isDarkMode={isDarkMode}
              statusLabel={String(presentation?.badge || presentation?.tooltip || "Tournament")}
              timerLabel={String(presentation?.timer || "").trim()}
              scopeEmoji={scope.emoji}
              registered={registered}
              registrationAction={registrationAction}
              onRegistrationAction={registrationAction ? () => void handleTournamentMapRegistration(tournament) : undefined}
              onBack={() => setSelectedTournamentMapDetail(null)}
              onClose={() => setSelectedTournamentMapDetail(null)}
              onOpenDock={() => {
                setSelectedTournamentMapDetail(null);
                setIsTournamentMapFilterOpen(false);
                setIsWorldTournamentMapOpen(false);
                if (typeof props.onTournamentClick === "function") {
                  props.onTournamentClick(id);
                } else {
                  props.onEventsClick?.({ from: "big_blue_tournament_detail" });
                }
              }}
            />
          );
        })() : null}

        {/* Big Blue tournament hub: one persistent entry point instead of many map pins. */}
        {isTournamentMapFilterOpen ? (
          <div
            className="absolute inset-0 z-[5200] bg-black/[0.04] pointer-events-auto dark:bg-black/[0.10]"
            onClick={() => setIsTournamentMapFilterOpen(false)}
            role="presentation"
          >
            <div
              onClick={(event) => event.stopPropagation()}
              className="pointer-events-auto absolute w-[min(76vw,360px)] max-h-[min(calc(var(--roddo-visual-vh,100dvh)-180px),520px)] overflow-hidden rounded-[22px] border shadow-[0_18px_50px_rgba(39,27,14,0.22)] backdrop-blur-xl dark:shadow-[0_20px_55px_rgba(0,0,0,0.48)]"
              style={{
                right: "66px",
                bottom: "calc(var(--roddo-map-bottom-clearance, var(--roddo-nav-h, 84px)) + env(safe-area-inset-bottom) + 66px)",
                background: isDarkMode ? "rgba(13,20,35,.97)" : "rgba(255,250,242,.98)",
                borderColor: isDarkMode ? "rgba(142,156,211,.20)" : "rgba(101,77,49,.13)",
              }}
            >
              <div className="relative z-[2] flex shrink-0 justify-center pt-2.5">
                <div
                  className="h-1 w-10 rounded-full sm:w-12"
                  style={{
                    background: isDarkMode
                      ? "rgba(255,255,255,0.20)"
                      : "rgba(80,62,45,0.16)",
                  }}
                />
              </div>
              <div className="flex items-center gap-3 border-b px-4 pb-3.5 pt-2.5 dark:border-white/10" style={{ borderColor: isDarkMode ? undefined : "rgba(101,77,49,.10)" }}>
                {String(
                  (tournamentHubRows[0]?.tournament as any)?.coverThumbUrl ||
                    (tournamentHubRows[0]?.tournament as any)?.coverImageUrl ||
                    "",
                ).trim() ? (
                  <div
                    className="relative h-10 w-12 shrink-0 overflow-hidden rounded-[13px] border"
                    style={{
                      borderColor: isDarkMode ? "rgba(232,201,122,.22)" : "rgba(174,119,17,.14)",
                      background: isDarkMode ? "#151D2E" : "#FFF3CB",
                    }}
                  >
                    <img
                      src={String(
                        (tournamentHubRows[0]?.tournament as any)?.coverThumbUrl ||
                          (tournamentHubRows[0]?.tournament as any)?.coverImageUrl ||
                          "",
                      )}
                      alt=""
                      className="absolute inset-0 h-full w-full object-cover"
                      style={{
                        objectPosition: `${Math.min(100, Math.max(0, Number((tournamentHubRows[0]?.tournament as any)?.coverImagePositionX ?? 50)))}% ${Math.min(100, Math.max(0, Number((tournamentHubRows[0]?.tournament as any)?.coverImagePositionY ?? 50)))}%`,
                        transform: `scale(${Math.min(1.6, Math.max(1, Number((tournamentHubRows[0]?.tournament as any)?.coverImageZoom || 1)))})`,
                        transformOrigin: `${Math.min(100, Math.max(0, Number((tournamentHubRows[0]?.tournament as any)?.coverImagePositionX ?? 50)))}% ${Math.min(100, Math.max(0, Number((tournamentHubRows[0]?.tournament as any)?.coverImagePositionY ?? 50)))}%`,
                      }}
                    />
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-white/5" />
                    <div
                      className="absolute bottom-0.5 left-0.5 grid h-4 w-4 place-items-center rounded-full border"
                      style={{
                        background: isDarkMode ? "rgba(16,24,41,.90)" : "rgba(255,250,235,.94)",
                        borderColor: isDarkMode ? "rgba(232,201,122,.35)" : "rgba(174,119,17,.22)",
                        color: isDarkMode ? "#FFE0A0" : "#A96E08",
                      }}
                    >
                      <HudTrophyIcon className="h-2.5 w-2.5" />
                    </div>
                  </div>
                ) : (
                  <div
                    className="grid h-10 w-10 shrink-0 place-items-center rounded-[13px] border"
                    style={{
                      background: isDarkMode ? "#151D2E" : "linear-gradient(145deg,#FFF1BD,#F4D06A)",
                      borderColor: isDarkMode ? "rgba(232,201,122,.24)" : "rgba(174,119,17,.16)",
                      color: isDarkMode ? "#FFE7A8" : "#9B680A",
                    }}
                  >
                    <HudTrophyIcon className="h-[22px] w-[22px]" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <div className="text-[15px] font-black" style={{ color: isDarkMode ? "#F2F6FC" : "#2D3957" }}>Tournaments</div>
                    <span
                      className="rounded-full border px-2 py-0.5 text-[9px] font-black"
                      style={{
                      background: isDarkMode ? "rgba(56,211,145,.16)" : "rgba(223,250,235,.98)",
                      borderColor: isDarkMode ? "rgba(96,232,169,.30)" : "rgba(45,174,105,.28)",
                      color: isDarkMode ? "#83F0BC" : "#137A49",
                      boxShadow: isDarkMode
                        ? "0 0 10px rgba(56,211,145,.12)"
                        : "0 3px 10px rgba(22,138,82,.08)",
                    }}
                    >
                      {tournamentHubRows.length} ACTIVE
                    </span>
                  </div>
                  <div className="mt-0.5 text-[10px] font-semibold opacity-60">Joined, live, open, and upcoming events</div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsTournamentMapFilterOpen(false)}
                  className="grid h-8 w-8 shrink-0 place-items-center rounded-full border text-[15px] font-black"
                  style={{
                    background: isDarkMode ? "#111B2D" : "rgba(255,255,255,.72)",
                    borderColor: isDarkMode ? "rgba(142,156,211,.16)" : "rgba(101,77,49,.12)",
                    color: isDarkMode ? "#D9E2F2" : "#655A4F",
                  }}
                  aria-label="Close tournaments"
                >×</button>
              </div>

              <div className="max-h-[min(calc(var(--roddo-visual-vh,100dvh)-320px),380px)] overflow-y-auto px-3 py-2.5">
                {tournamentHubRows.length ? (
                  <div className="space-y-1.5">
                    {tournamentHubRows.map(({ tournament, presentation }: any) => {
                      const id = String(tournament?.id || "");
                      const statusLabel = [presentation?.badge || presentation?.tooltip, presentation?.timer].filter(Boolean).join(" · ");
                      const locationLabel = isWorldwideTournament(tournament)
                        ? "Worldwide"
                        : String(tournament?.locationLabel || tournament?.mapLocation?.label || "Tournament area");
                      const fee = Number(tournament?.entryFeeShells ?? tournament?.entryFee ?? tournament?.shellEntryFee ?? 0);
                      const feeLabel = Number.isFinite(fee) && fee > 0 ? `${Math.round(fee)} shells` : "Free entry";
                      const approvedCountRaw = Number(tournament?.approvedParticipantCount);
                      const hasApprovedCount = Number.isFinite(approvedCountRaw) && approvedCountRaw >= 0;
                      const approvedCount = hasApprovedCount ? Math.max(0, Math.round(approvedCountRaw)) : 0;
                      const maxCountRaw = Number(tournament?.maxParticipants);
                      const hasMaxCount = Number.isFinite(maxCountRaw) && maxCountRaw > 0;
                      const anglerCountLabel = hasApprovedCount
                        ? hasMaxCount
                          ? `${approvedCount}/${Math.round(maxCountRaw)} anglers`
                          : `${approvedCount} angler${approvedCount === 1 ? "" : "s"}`
                        : "";
                      const toneColor = presentation?.tone === "live"
                        ? (isDarkMode ? "#78E6B0" : "#168A52")
                        : presentation?.tone === "open"
                          ? (isDarkMode ? "#FFE36E" : "#B77900")
                          : presentation?.tone === "finished"
                            ? (isDarkMode ? "#F6C85F" : "#A86800")
                            : presentation?.tone === "joined" || presentation?.tone === "pending"
                              ? (isDarkMode ? "#9FD9FF" : "#2F80ED")
                              : (isDarkMode ? "#D7DFEA" : "#6E6257");
                      const coverUrl = String(tournament?.coverThumbUrl || tournament?.coverImageUrl || "").trim();
                      const coverX = Math.min(100, Math.max(0, Number(tournament?.coverImagePositionX ?? 50)));
                      const coverY = Math.min(100, Math.max(0, Number(tournament?.coverImagePositionY ?? 50)));
                      const coverZoom = Math.min(1.6, Math.max(1, Number(tournament?.coverImageZoom || 1)));
                      const isLiveTournament = presentation?.tone === "live";
                      const scopeBadge = getTournamentScopeBadge(tournament);
                      const isRegisteredTournament =
                        String(myTournamentParticipation[id]?.status || "").toLowerCase() === "approved";

                      return (
                        <button
                          key={id}
                          type="button"
                          onClick={() => {
                            if (presentation?.badge === "RESULTS") {
                              markTournamentResultsSeen(String((props.userProfile as any)?.uid || ""), id);
                              setTournamentResultsSeenRevision((value) => value + 1);
                            }
                            setIsTournamentMapFilterOpen(false);
                            setSelectedTournamentMapDetail(tournament);
                          }}
                          className={`group flex min-h-[70px] w-full items-center gap-3 rounded-[15px] border px-2.5 py-2.5 text-left transition active:scale-[.99] ${isLiveTournament ? "roddo-tournament-live-row" : ""}`}
                          style={{
                            background: isDarkMode ? "rgba(17,27,45,.82)" : "rgba(255,255,255,.72)",
                            borderColor: isLiveTournament
                              ? (isDarkMode ? "rgba(72,211,148,.34)" : "rgba(22,138,82,.28)")
                              : (isDarkMode ? "rgba(142,156,211,.12)" : "rgba(101,77,49,.09)"),
                            boxShadow: isLiveTournament
                              ? (isDarkMode ? "0 0 0 1px rgba(72,211,148,.08), 0 8px 22px rgba(0,0,0,.12)" : "0 7px 18px rgba(22,138,82,.10)")
                              : undefined,
                          }}
                        >
                          {coverUrl ? (
                            <div className="relative h-14 w-[68px] shrink-0 overflow-visible">
                              <div
                                className="relative h-full w-full overflow-hidden rounded-[12px] border"
                                style={{
                                  borderColor: isDarkMode ? "rgba(232,201,122,.18)" : "rgba(101,77,49,.10)",
                                  background: isDarkMode ? "#111A2B" : "#F7EFE4",
                                }}
                              >
                                <img
                                  src={coverUrl}
                                  alt=""
                                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
                                  style={{
                                    objectPosition: `${coverX}% ${coverY}%`,
                                    transform: `scale(${coverZoom})`,
                                    transformOrigin: `${coverX}% ${coverY}%`,
                                  }}
                                />
                                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-white/5" />
                              </div>
                              {isRegisteredTournament ? (
                                <div
                                  className="absolute z-[3] grid h-5 w-5 place-items-center rounded-full border text-[11px] font-black leading-none"
                                  title="Registered"
                                  aria-label="Registered"
                                  style={{
                                    left: "-6px",
                                    top: "-6px",
                                    background: "rgba(47,128,237,.98)",
                                    borderColor: isDarkMode ? "rgba(204,231,255,.86)" : "rgba(255,255,255,.96)",
                                    color: "#FFFFFF",
                                    boxShadow: isDarkMode
                                      ? "0 3px 10px rgba(20,68,128,.42), 0 0 0 1px rgba(47,128,237,.12)"
                                      : "0 3px 9px rgba(20,68,128,.26)",
                                  }}
                                >
                                  ✓
                                </div>
                              ) : null}
                              <div
                                className="absolute z-[3] grid h-5 w-5 place-items-center rounded-full border text-[10px] leading-none"
                                title={scopeBadge.label}
                                aria-label={scopeBadge.label}
                                style={{
                                  right: "-6px",
                                  top: "-6px",
                                  background: isDarkMode ? "rgba(10,17,32,.97)" : "rgba(255,250,238,.98)",
                                  borderColor: isDarkMode ? "rgba(255,224,160,.46)" : "rgba(174,119,17,.28)",
                                  boxShadow: isDarkMode
                                    ? "0 3px 10px rgba(0,0,0,.34)"
                                    : "0 3px 9px rgba(86,57,18,.18)",
                                }}
                              >
                                <span aria-hidden="true">{scopeBadge.emoji}</span>
                              </div>
                            </div>
                          ) : (
                            <div className="relative h-14 w-[68px] shrink-0 overflow-visible">
                              <div
                                className="grid h-full w-full place-items-center overflow-hidden rounded-[12px] border"
                                style={{
                                  background: isDarkMode ? "#151D2E" : "linear-gradient(145deg,#FFF3CB,#F6D77B)",
                                  borderColor: isDarkMode ? "rgba(232,201,122,.20)" : "rgba(186,132,24,.12)",
                                  color: isDarkMode ? "#FFE0A0" : "#B77A0D",
                                }}
                              >
                                <HudTrophyIcon className="h-6 w-6" />
                              </div>
                              {isRegisteredTournament ? (
                                <div
                                  className="absolute z-[3] grid h-5 w-5 place-items-center rounded-full border text-[11px] font-black leading-none"
                                  title="Registered"
                                  aria-label="Registered"
                                  style={{
                                    left: "-6px",
                                    top: "-6px",
                                    background: "rgba(47,128,237,.98)",
                                    borderColor: isDarkMode ? "rgba(204,231,255,.86)" : "rgba(255,255,255,.96)",
                                    color: "#FFFFFF",
                                    boxShadow: isDarkMode
                                      ? "0 3px 10px rgba(20,68,128,.42), 0 0 0 1px rgba(47,128,237,.12)"
                                      : "0 3px 9px rgba(20,68,128,.26)",
                                  }}
                                >
                                  ✓
                                </div>
                              ) : null}
                              <div
                                className="absolute z-[3] grid h-5 w-5 place-items-center rounded-full border text-[10px] leading-none"
                                title={scopeBadge.label}
                                aria-label={scopeBadge.label}
                                style={{
                                  right: "-6px",
                                  top: "-6px",
                                  background: isDarkMode ? "rgba(10,17,32,.97)" : "rgba(255,250,238,.98)",
                                  borderColor: isDarkMode ? "rgba(255,224,160,.46)" : "rgba(174,119,17,.28)",
                                  boxShadow: isDarkMode
                                    ? "0 3px 10px rgba(0,0,0,.34)"
                                    : "0 3px 9px rgba(86,57,18,.18)",
                                }}
                              >
                                <span aria-hidden="true">{scopeBadge.emoji}</span>
                              </div>
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-[12px] font-black" style={{ color: isDarkMode ? "#F2F6FC" : "#2D3957" }}>{String(tournament?.name || "Roddo Tournament")}</div>
                            <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-[9.5px] font-bold">
                              <span style={{ color: toneColor }}>{statusLabel || "Tournament"}</span>
                              <span className="opacity-35">•</span>
                              <span className="truncate opacity-55">{locationLabel}</span>
                            </div>
                            <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-[9px] font-semibold opacity-45">
                              <span>{feeLabel}</span>
                              {anglerCountLabel ? <><span>•</span><span>👥 {anglerCountLabel}</span></> : null}
                            </div>
                          </div>
                          <div className="text-lg font-black opacity-35">›</div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="px-4 py-8 text-center">
                    <div className="text-[13px] font-black" style={{ color: isDarkMode ? "#F2F6FC" : "#2D3957" }}>No current tournaments</div>
                    <div className="mt-1 text-[10px] font-semibold opacity-55">Open Tournament Dock to browse or host one.</div>
                  </div>
                )}
              </div>

              <div className="border-t p-3 dark:border-white/10" style={{ borderColor: isDarkMode ? undefined : "rgba(101,77,49,.10)" }}>
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={() => {
                      setIsTournamentMapFilterOpen(false);
                      props.onEventsClick?.({ from: "big_blue_tournament_hub" });
                    }}
                    className="inline-flex h-10 items-center justify-center rounded-[13px] border px-4 text-[11px] font-black transition hover:-translate-y-px active:translate-y-0 active:scale-[0.99]"
                    style={{
                      minWidth: "168px",
                      background: isDarkMode ? "#F0C86D" : "#2F80ED",
                      borderColor: isDarkMode ? "rgba(240,200,109,0.30)" : "rgba(47,128,237,0.26)",
                      color: isDarkMode ? "#1A2233" : "#FFFFFF",
                      boxShadow: isDarkMode
                        ? "0 6px 14px rgba(240,200,109,0.16)"
                        : "0 6px 14px rgba(47,128,237,0.14)",
                    }}
                  >
                    Open Tournament Dock
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

  </>);
};
export default BigBlueTournamentOverlays;
